import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import RouteSkeleton from "@/components/RouteSkeleton";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import SkillControlsPanel from "../SkillControlsPanel";
import IconByName from "@/components/ds/IconByName";
import { Check } from "@/components/ds/icons";

interface VastConfig {
  configured: boolean;
  id?: number;
  displayName?: string;
  baseUrl?: string;
  locationId?: string;
  apiKeyMasked?: string;
  isEnabled?: boolean;
  pushTranscripts?: boolean;
  createdAt?: string;
}

const CAPABILITIES = [
  {
    title: "Customer & Vehicle lookup",
    icon: "User",
    items: [
      "Find any VAST customer instantly by phone number or email — name, vehicle count, and account history returned in seconds",
      "List all vehicles on the customer's account with year, make, model, trim, VIN, and current mileage",
      "Pull full service history — last N visits with date, service type, technician, and total amount",
      "Look up OEM tire spec by year/make/model/trim — front and rear sizes, load index, speed rating"
    ]
  },
  {
    title: "Tire inventory & pricing",
    icon: "Package",
    items: [
      "Search tire inventory by vehicle, size, brand, or category — returns available tires with stock counts",
      "Get real-time pricing including retail, sale, and install price for any tire + quantity combination",
      "Check inventory levels at specific locations for multi-location dealers",
      "Surface active promotions and rebates automatically in customer conversations"
    ]
  },
  {
    title: "Appointments & work orders",
    icon: "Calendar",
    items: [
      "Show available appointment slots for any service type and date range — lets customers self-book 24/7",
      "Book, cancel, and reschedule appointments directly from chat or IVR without agent involvement",
      "Create work orders and check repair status — 'Is my car ready?' answered instantly",
      "Generate service estimates and retrieve existing quotes on demand"
    ]
  },
  {
    title: "AI workflow tools (20)",
    icon: "Zap",
    items: [
      "20 AI tools available in every workflow and bot: customer (2), vehicles (2), tires (5), appointments (5), work orders (3), estimates (2), warranty (1)",
      "No workflow configuration required — the AI decides when to call VAST based on conversation context",
      "Screen pop: agent sidebar shows VAST customer record automatically on every inbound call or chat"
    ]
  }
];

const AI_TOOLS = [
  { name: "vast_lookup_customer_phone", label: "Look up customer by phone", group: "Customer", description: "Find a VAST customer by phone number." },
  { name: "vast_lookup_customer_email", label: "Look up customer by email", group: "Customer", description: "Find a VAST customer by email address." },
  { name: "vast_get_vehicles", label: "Get customer vehicles", group: "Vehicles", description: "List vehicles on file for a customer." },
  { name: "vast_get_service_history", label: "Get service history", group: "Vehicles", description: "Recent service visits with dates and work done." },
  { name: "vast_lookup_oem_tire_size", label: "OEM tire size lookup", group: "Tires", description: "OEM tire spec by year/make/model/trim." },
  { name: "vast_search_tire_inventory", label: "Search tire inventory", group: "Tires", description: "Search by size, brand, vehicle, or category." },
  { name: "vast_get_tire_pricing", label: "Get tire pricing", group: "Tires", description: "Price and availability for a tire + quantity." },
  { name: "vast_get_inventory_level", label: "Check tire stock", group: "Tires", description: "Stock count at a location." },
  { name: "vast_get_promotions", label: "Active promotions", group: "Tires", description: "Current deals and rebates." },
  { name: "vast_get_available_slots", label: "Available appointment times", group: "Appointments", description: "Open slots for a service type and date range." },
  { name: "vast_get_appointments", label: "Get appointments", group: "Appointments", description: "Customer's upcoming appointments." },
  { name: "vast_book_appointment", label: "Book appointment", group: "Appointments", description: "Book a service appointment.", destructive: false },
  { name: "vast_cancel_appointment", label: "Cancel appointment", group: "Appointments", description: "Cancel an appointment.", destructive: true },
  { name: "vast_reschedule_appointment", label: "Reschedule appointment", group: "Appointments", description: "Move appointment to a new slot." },
  { name: "vast_create_work_order", label: "Create work order", group: "Work Orders", description: "Open a work order for services." },
  { name: "vast_get_work_order_status", label: "Work order status", group: "Work Orders", description: "Check repair/service status." },
  { name: "vast_get_work_orders", label: "Get work orders", group: "Work Orders", description: "Recent work orders for a customer." },
  { name: "vast_create_estimate", label: "Create estimate", group: "Estimates", description: "Generate a service estimate." },
  { name: "vast_get_estimate", label: "Get estimate", group: "Estimates", description: "Retrieve an existing estimate." },
  { name: "vast_get_warranty_info", label: "Tire warranty info", group: "Warranty", description: "Warranty details for a tire." }
];

type Tab = "overview" | "config" | "skills" | "activity";

export default function Vast() {
  const api = useApi();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [config, setConfig] = useState<VastConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form, setForm] = useState({ apiKey: "", apiSecret: "", baseUrl: "", locationId: "", displayName: "", pushTranscripts: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get("Vast");
      if (resp.ok) setConfig(await resp.json());
    } catch { setConfig(null); } finally { setLoading(false); }
  }, [api.messaging]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await api.messaging.post("Vast", form);
      if (resp.ok) { await load(); setTab("overview"); }
      else setSaveError("Failed to save. Please check your settings and try again.");
    } catch { setSaveError("Network error. Please try again."); } finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!config?.id) return;
    try {
      await api.messaging.delete(`Vast/${config.id}`);
      await load();
    } catch { /* non-fatal */ }
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "config", label: config?.configured ? "Settings" : "Connect" },
    { key: "skills", label: "AI Tools" },
    { key: "activity", label: "Activity" }
  ];

  if (loading) return <RouteSkeleton />;

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center"><IconByName name="Briefcase" className="w-6 h-6 text-blue-700" /></div>
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">VAST Retail</h1>
          <p className="text-sm text-slate-500">by Kerridge CS — Tire Shop DMS</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {config?.configured && (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">
              ● Connected
            </span>
          )}
          <LaunchWizardButton onClick={() => setShowWizard(true)} />
        </div>
      </div>

      {/* API pending notice */}
      <div className="mb-5 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
        <strong>Partner API pending.</strong> VAST Retail requires a Kerridge CS partner agreement for API access.
        Contact <strong>partnerships@kerridgecs.com</strong> to request sandbox credentials.
        All configuration and AI tools are ready — they activate automatically once credentials are wired.
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-6 gap-1">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === "overview" && (
        <div className="space-y-6">
          {CAPABILITIES.map(cap => (
            <div key={cap.title} className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <IconByName name={cap.icon} className="w-5 h-5" />
                <h3 className="font-semibold text-slate-900 dark:text-white">{cap.title}</h3>
              </div>
              <ul className="space-y-1">
                {cap.items.map((item, i) => (
                  <li key={i} className="text-sm text-slate-600 flex gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Config */}
      {tab === "config" && (
        <div className="max-w-lg space-y-4">
          {config?.configured && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
              Connected{config.displayName ? ` — ${config.displayName}` : ""}
              {config.apiKeyMasked && <span className="ml-2 text-green-600">Key: {config.apiKeyMasked}</span>}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">API Key <span className="text-red-500">*</span></label>
            <input type="password" value={form.apiKey} onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))}
              placeholder="From Kerridge CS partner portal"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">API Secret</label>
            <input type="password" value={form.apiSecret} onChange={e => setForm(f => ({ ...f, apiSecret: e.target.value }))}
              placeholder="From Kerridge CS partner portal"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">API Base URL <span className="text-red-500">*</span></label>
            <input type="text" value={form.baseUrl} onChange={e => setForm(f => ({ ...f, baseUrl: e.target.value }))}
              placeholder="https://api.kerridgecs.com/vast/v1/dealer/your-id"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-xs text-slate-500 mt-1">Dealer-specific endpoint provided by Kerridge CS on partner onboarding.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Default Location ID</label>
            <input type="text" value={form.locationId} onChange={e => setForm(f => ({ ...f, locationId: e.target.value }))}
              placeholder="Optional — for multi-location dealers"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Display Name</label>
            <input type="text" value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
              placeholder="e.g. Bob's Tire Shop VAST"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="pushTranscripts" checked={form.pushTranscripts}
              onChange={e => setForm(f => ({ ...f, pushTranscripts: e.target.checked }))}
              className="rounded border-slate-300" />
            <label htmlFor="pushTranscripts" className="text-sm text-slate-700">Push chat transcripts to VAST customer record on conversation end</label>
          </div>
          {saveError && <p className="text-sm text-red-600">{saveError}</p>}
          <div className="flex gap-2">
            <button onClick={save} disabled={saving || !form.apiKey || !form.baseUrl}
              className="px-4 py-2 bg-[var(--color-primary)] text-white text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-50 transition-colors">
              {saving ? "Saving…" : config?.configured ? "Update" : "Save"}
            </button>
            {config?.configured && (
              <button onClick={disconnect}
                className="px-4 py-2 border border-red-300 text-red-600 text-sm font-medium rounded-md hover:bg-red-50 transition-colors">
                Disconnect
              </button>
            )}
          </div>
        </div>
      )}

      {/* Skills */}
      {tab === "skills" && (
        <SkillControlsPanel
          integration="vast"
          skills={AI_TOOLS}
        />
      )}

      {/* Activity */}
      {tab === "activity" && <IntegrationActivityLog integrationSlug="Vast" />}
    </div>
  );
}

export function BackToIntegrationLandingVast() {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate("/integrations")}
      className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
      ← Integrations
    </button>
  );
}
